import React, { Fragment, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Dialog, Transition } from '@headlessui/react';
import { BookmarkIcon, XIcon } from 'lucide-react';
import { saveUserSearch } from "shared/api/api";
import { useSelector } from "react-redux";
import { useTranslation } from 'react-i18next';

const SaveSearchButton = ({ className }) => {
    const { t } = useTranslation('common');
    const isAuthenticated = useSelector((state) => state.auth.isAuthenticated);

    const [isOpen, setIsOpen] = useState(false);
    const [name, setName] = useState('');
    const [error, setError] = useState('');
    const [success, setSuccess] = useState(false);
    const location = useLocation();

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setSuccess(false);

        try {
            const searchData = {
                name: name,
                url: window.location.href
            };

            await saveUserSearch(searchData);
            setSuccess(true);
            setTimeout(() => {
                setIsOpen(false);
                setName('');
                setSuccess(false);
            }, 1500);
        } catch (err) {
            setError(t('search.saveSearch.error', 'Der opstod en fejl. Prøv igen.'));
        }
    };

    // Default button style for desktop
    const defaultButtonClass = "inline-flex ml-4 items-center gap-x-2 rounded-md border border-sky-600 px-3.5 py-2.5 text-sm font-semibold text-sky-600 shadow-sm hover:bg-sky-500 hover:text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sky-600";
    
    // Use provided className or default
    const buttonClass = className || defaultButtonClass;

    return (
        isAuthenticated && (
            <>
                <button
                    onClick={() => setIsOpen(true)}
                    type="button"
                    className={buttonClass}
                >
                    <BookmarkIcon className="h-5 w-5" />
                    <span>{t('search.saveSearch.title', 'Gem søgning')}</span>
                </button>

                <Transition show={isOpen} as={Fragment}>
                    <Dialog onClose={() => setIsOpen(false)} className="relative z-50">
                        <Transition.Child
                            as={Fragment}
                            enter="ease-out duration-300"
                            enterFrom="opacity-0"
                            enterTo="opacity-100"
                            leave="ease-in duration-200"
                            leaveFrom="opacity-100"
                            leaveTo="opacity-0"
                        >
                            <div className="fixed inset-0 bg-black/30" />
                        </Transition.Child>

                        <div className="fixed inset-0 overflow-y-auto">
                            <div className="flex min-h-full items-center justify-center p-4">
                                <Transition.Child
                                    as={Fragment}
                                    enter="ease-out duration-300"
                                    enterFrom="opacity-0 scale-95"
                                    enterTo="opacity-100 scale-100"
                                    leave="ease-in duration-200"
                                    leaveFrom="opacity-100 scale-100"
                                    leaveTo="opacity-0 scale-95"
                                >
                                    <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-lg bg-white p-6 shadow-xl transition-all">
                                        <div className="flex items-center justify-between mb-4">
                                            <Dialog.Title className="text-lg font-medium text-gray-900">
                                                {t('search.saveSearch.title', 'Gem søgning')}
                                            </Dialog.Title>
                                            <button
                                                onClick={() => setIsOpen(false)}
                                                className="text-gray-400 hover:text-gray-500"
                                            >
                                                <XIcon className="h-6 w-6" />
                                            </button>
                                        </div>

                                        <form onSubmit={handleSubmit} className="space-y-4">
                                            <div>
                                                <label htmlFor="name" className="block text-sm font-medium text-gray-700">
                                                    {t('search.saveSearch.nameLabel', 'Navn på søgning')}
                                                </label>
                                                <input
                                                    type="text"
                                                    id="name"
                                                    value={name}
                                                    onChange={(e) => setName(e.target.value)}
                                                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-sky-500 focus:ring-sky-500 sm:text-sm"
                                                    placeholder={t('search.saveSearch.namePlaceholder', 'Indtast navn på din søgning')}
                                                    required
                                                />
                                            </div>

                                            <div className="hidden">
                                                <label className="block text-sm font-medium text-gray-700">
                                                    {t('search.saveSearch.urlLabel', 'Søge-URL')}
                                                </label>
                                                <div className="mt-1 rounded-md bg-gray-50 p-3 text-sm break-all text-gray-600">
                                                    {window.location.href}
                                                </div>
                                            </div>

                                            {error && (
                                                <div className="rounded-md bg-red-50 p-4">
                                                    <div className="text-sm text-red-700">{error}</div>
                                                </div>
                                            )}

                                            {success && (
                                                <div className="rounded-md bg-green-50 p-4">
                                                    <div className="text-sm text-green-700">
                                                        {t('search.saveSearch.success', 'Din søgning er gemt!')}
                                                    </div>
                                                </div>
                                            )}

                                            <div className="mt-5 flex justify-end space-x-3">
                                                <button
                                                    type="button"
                                                    onClick={() => setIsOpen(false)}
                                                    className="inline-flex justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-sky-500 focus:ring-offset-2"
                                                >
                                                    {t('common.cancel', 'Annuller')}
                                                </button>
                                                <button
                                                    type="submit"
                                                    className="inline-flex justify-center rounded-md border border-transparent bg-sky-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-sky-500 focus:outline-none focus:ring-2 focus:ring-sky-500 focus:ring-offset-2"
                                                >
                                                    {t('search.saveSearch.submit', 'Gem')}
                                                </button>
                                            </div>
                                        </form>
                                    </Dialog.Panel>
                                </Transition.Child>
                            </div>
                        </div>
                    </Dialog>
                </Transition>
            </>
        )
    );
};

export default SaveSearchButton;